import baseConfig from "@/app/config/hitseekerPro"
import { useSubDomain } from "@/app/hooks"
import { AppConfigType } from "@/model/appConfig"
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

const ConfigContext = createContext<AppConfigType & { isLoading: boolean }>({
  ...baseConfig,
  isLoading: false,
})

export const ApplicationProvider = ({ children }: { children: ReactNode }) => {
  const { clientName } = useSubDomain()

  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [config, setConfig] = useState<AppConfigType>(baseConfig)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const configData = await import(`../app/config/${clientName}.ts`)
        setConfig(configData.config)
      } catch (error) {
        console.error("Error loading configuration:", error)
      } finally {
        setIsLoadingConfig(false)
      }
    }

    loadConfig()
  }, [clientName])

  const providerValue = useMemo(
    () => ({
      ...config,
      isLoading: isLoadingConfig,
    }),
    [config, isLoadingConfig],
  )

  return (
    <ConfigContext.Provider value={providerValue}>
      {children}
    </ConfigContext.Provider>
  )
}

export const useAppConfig = () => {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error("useAppConfig must be used within a ConfigProvider")
  }
  return context
}
